import { onMounted, ref, readonly } from 'vue';

const DEFAULT_FONT_SIZE = 16;
const SCALE_DECIMALS = 4;

const layoutScale = ref(1);
const baseFontSize = ref(DEFAULT_FONT_SIZE);
let baselineFontSize = DEFAULT_FONT_SIZE;
let hasInitialized = false;

const isClient = typeof window !== 'undefined';

function detectBaselineFontSize(): number {
  if (!isClient) {
    return DEFAULT_FONT_SIZE;
  }

  const root = document.documentElement;
  const computedFontSize = window.getComputedStyle(root).fontSize;
  const parsedSize = Number.parseFloat(computedFontSize);

  return Number.isNaN(parsedSize) ? DEFAULT_FONT_SIZE : parsedSize;
}

function deriveOperatingSystemScale(): number {
  if (!isClient) {
    return 1;
  }

  const isWindowsPlatform = /windows/i.test(navigator.userAgent);
  if (!isWindowsPlatform) {
    return 1;
  }

  const viewportScale = window.visualViewport?.scale ?? 1;
  const rawDevicePixelRatio = window.devicePixelRatio || 1;
  const osPixelRatio = rawDevicePixelRatio / viewportScale;

  if (osPixelRatio <= 1) {
    return 1;
  }

  return Number((1 / osPixelRatio).toFixed(SCALE_DECIMALS));
}

function applyScale(scale: number): void {
  if (!isClient) {
    return;
  }

  const root = document.documentElement;
  const nextFontSize = baselineFontSize * scale;

  layoutScale.value = scale;
  baseFontSize.value = nextFontSize;

  root.style.fontSize = `${nextFontSize}px`;
  root.style.setProperty('--app-scale-factor', scale.toFixed(SCALE_DECIMALS));
  root.style.setProperty('--app-base-font-size', `${nextFontSize}px`);
}

function updateScale(): void {
  const scale = deriveOperatingSystemScale();
  applyScale(scale);
}

function initializeOnce(): void {
  if (hasInitialized || !isClient) {
    return;
  }

  baselineFontSize = detectBaselineFontSize();
  baseFontSize.value = baselineFontSize;

  updateScale();

  const handleResize = (): void => {
    updateScale();
  };

  window.addEventListener('resize', handleResize);
  window.visualViewport?.addEventListener('resize', handleResize);

  hasInitialized = true;
}

export function useLayoutScale() {
  if (!hasInitialized) {
    initializeOnce();
  }

  onMounted(() => {
    if (!hasInitialized) {
      initializeOnce();
    }
  });

  return {
    scale: readonly(layoutScale),
    baseFontSize: readonly(baseFontSize),
  };
}
